import {Dispatch, SetStateAction, useMemo, useState} from 'react';
import {BottomSheet} from 'react-spring-bottom-sheet';
import Button from '../../../../../../components/button';
import InputField from '../../../../../../components/simpleInput';
import SpinnerLoading from '../../../../../../components/SpinnerLoading';
import useDeleteAccountNumber from '../../../../../../services/hookService/deleteAccountNumber/useDeleteAccountNumber';
import usePostAccountNumber from '../../../../../../services/hookService/postAccountNumbers/usePostAccountNumber';

interface IProps {
  shouldOpen: boolean;
  setShouldOpen: Dispatch<SetStateAction<boolean>>;
  accountNumbersData?: {
    bank_account_number: string;
    iban_number: string;
    bank_title: string;
    guid: string;
  };
}

const MAX_LENGTH = 24;

const AddAccountNumberBottomSheet = ({
  shouldOpen,
  setShouldOpen,
  accountNumbersData,
}: IProps) => {
  const [shabaNumber, setShabaNumber] = useState<string | null>(null);
  const [accountNumber, setAccountNumber] = useState<string | null>(null);

  const normalizedAccount = accountNumber?.trim() ?? '';
  const normalizedShaba = shabaNumber?.trim() ?? '';

  const isAccountTooLong = normalizedAccount.length > MAX_LENGTH;
  const isShabaTooLong = normalizedShaba.length > MAX_LENGTH;

  const {mutate: postAccountNumber, isLoading} = usePostAccountNumber(() => {
    setAccountNumber(null);
    setShabaNumber(null);
    setShouldOpen(false);
  });
  const {mutate: deleteAccountNumber, isLoading: isDeleting} =
    useDeleteAccountNumber(() => {
      setAccountNumber(null);
      setShabaNumber(null);
      setShouldOpen(false);
    });

  const handleSubmitAccountForm = () => {
    if (!accountNumber || !shabaNumber) return;

    if (accountNumbersData?.bank_account_number) {
      deleteAccountNumber({
        guid: accountNumbersData?.guid,
      });
      postAccountNumber({
        bank_account_number: accountNumber,
        iban_number: shabaNumber,
        bank_title: 'اسم بانک',
      });
    }

    if (!accountNumbersData?.bank_account_number) {
      postAccountNumber({
        bank_account_number: accountNumber,
        iban_number: shabaNumber,
        bank_title: 'اسم بانک',
      });
    }
  };

  const isSubmitDisabled = useMemo(
    () =>
      !normalizedAccount ||
      !normalizedShaba ||
      isAccountTooLong ||
      isShabaTooLong ||
      isLoading ||
      isDeleting,
    [
      normalizedAccount,
      normalizedShaba,
      isAccountTooLong,
      isShabaTooLong,
      isLoading,
      isDeleting,
    ],
  );

  return (
    <BottomSheet
      open={shouldOpen}
      blocking
      onDismiss={() => !isLoading && setShouldOpen(false)}
      className="custom-bottom-sheet"
    >
      <div className="relative">
        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/60 backdrop-blur-sm">
            <SpinnerLoading size={36} />
          </div>
        )}

        <div
          className={`relative z-10 flex flex-col w-full gap-2 p-6 ${
            isLoading ? 'blur-sm pointer-events-none select-none' : ''
          }`}
        >
          <i
            className="absolute text-2xl text-gray-500 icon-nameClose-remove top-6 left-6"
            onClick={() => !isLoading && setShouldOpen(false)}
          />

          <p className="flex justify-center w-full mt-1 text-lg font-medium text-zinc-800">
            افزودن شماره حساب
          </p>

          <div className="flex flex-col gap-2">
            <InputField
              label="شماره حساب"
              labelClassName="text-sm pr-1"
              name="accountNumber"
              inputMode="numeric"
              placeholder="شماره حساب را وارد کنید"
              required
              value={normalizedAccount}
              onValueChange={
                val =>
                  setAccountNumber(val.replace(/\D/g, '').slice(0, MAX_LENGTH)) // only let numbers
              }
            />
            {isAccountTooLong && (
              <span className="text-xs text-red-500">
                شماره حساب نباید بیشتر از ۲۴ کاراکتر باشد
              </span>
            )}

            <InputField
              label="شماره شبا"
              labelClassName="text-sm pr-1"
              name="shabaNumber"
              inputMode="numeric"
              placeholder="شماره شبا را وارد کنید"
              required
              value={normalizedShaba}
              onValueChange={
                val =>
                  setShabaNumber(val.replace(/\D/g, '').slice(0, MAX_LENGTH)) // only let numbers
              }
            />
            {isShabaTooLong && (
              <span className="text-xs text-red-500">
                شماره شبا نباید بیشتر از ۲۴ کاراکتر باشد
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 mt-4">
            <Button
              className="flex items-center w-full gap-2 text-white bg-farm-shop-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
              disabled={isSubmitDisabled}
              onClick={handleSubmitAccountForm}
            >
              تایید
            </Button>
          </div>
        </div>
      </div>
    </BottomSheet>
  );
};

export default AddAccountNumberBottomSheet;
